import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = getCategoryBySlug(slug || 'whisper');
  const [activeSlug, setActiveSlug] = useState(slug || 'whisper');

  const displayCategory = activeSlug !== slug
    ? getCategoryBySlug(activeSlug) || currentCategory
    : currentCategory;

  const categoryArticles = useMemo(
    () => (displayCategory ? getArticlesByCategory(displayCategory.slug) : []),
    [displayCategory]
  );

  if (!displayCategory) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <p className="text-foreground-600">分类未找到</p>
      </div>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://asmrone.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '分类浏览',
        item: 'https://asmrone.com/category/whisper',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: displayCategory.name,
        item: `https://asmrone.com/category/${displayCategory.slug}`,
      },
    ],
  };

  return (
    <>
      <SEOMeta
        title={`${displayCategory.name} - 分类浏览`}
        description={`探索 asmrone ${displayCategory.name}分类下的精选 ASMR 音频内容。${displayCategory.description}`}
        keywords={`asmrone,${displayCategory.name},ASMR音频,ASMR分类`}
        canonicalUrl={`https://asmrone.com/category/${displayCategory.slug}`}
        schema={breadcrumbSchema}
      />

      <main className="min-h-screen pt-16">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/category/whisper" className="hover:text-primary-600 transition-colors">分类浏览</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">{displayCategory.name}</span>
          </nav>

          <div className="relative rounded-lg overflow-hidden mb-10">
            <div className="aspect-[21/9]">
              <img
                src={displayCategory.image}
                alt={displayCategory.name}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent"></div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 backdrop-blur-sm">
                  <i className={`${displayCategory.icon} text-xl text-background-50`}></i>
                </div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50">
                  {displayCategory.name}
                </h1>
              </div>
              <p className="text-sm md:text-base text-background-50/80 max-w-2xl">
                {displayCategory.description}
              </p>
            </div>
          </div>

          <div className="mb-10">
            <div className="flex items-center gap-2 flex-wrap" role="tablist">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  role="tab"
                  aria-selected={activeSlug === cat.slug}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <p className="text-sm text-foreground-500">
              共 <strong className="text-foreground-700">{categoryArticles.length}</strong> 个音频内容
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group rounded-lg bg-background-50 border border-background-200/60 hover:border-primary-200/50 transition-all overflow-hidden"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="text-xs text-foreground-500">{article.duration}</span>
                    <span className="text-xs text-foreground-400">·</span>
                    <span className="text-xs text-foreground-500">{article.date}</span>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-500">{article.author}</span>
                    <span className="flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-headphone-line"></i>
                      {article.listenCount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {categoryArticles.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无内容</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}